import { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";

const TYPE_META = {
  Hematology:  { color: "#2563EB", badge: "badge-blue"   },
  Hormone:     { color: "#7C3AED", badge: "badge-purple" },
  Immunology:  { color: "#0D9488", badge: "badge-teal"   },
  Biochemical: { color: "#16A34A", badge: "badge-green"  },
  Other:       { color: "#64748B", badge: "badge-gray"   },
};

const TypeDot = ({ type }) => {
  const color = TYPE_META[type]?.color || "#64748B";
  return <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, display: "inline-block", marginRight: 6, flexShrink: 0 }} />;
};

const ROLE_LABEL = { admin: "Administrator", doctor: "Doctor", technician: "Technician", receptionist: "Receptionist" };

export default function Dashboard({ setActive, setSelectedPatient }) {
  const { patients, user, pagination } = useApp();
  const [dashStats, setDashStats] = useState(null);
  const [patientTotal, setPatientTotal] = useState(0);

  useEffect(() => {
    import("../utils/api").then(({ reportAPI, patientAPI }) => {
      Promise.all([
        reportAPI.list({ page: 1, pageSize: 5 }),
        patientAPI.list("", 1, 1),
      ]).then(([repData, patData]) => {
        setDashStats(repData);
        setPatientTotal(patData.total ?? 0);
      }).catch(() => {});
    });
  }, []);

  const totalPatients = patientTotal || pagination?.total || patients.length;
  const totalReports  = dashStats?.stats?.total ?? 0;
  const thisMonth     = new Date().toISOString().slice(0, 7);
  const newThisMonth  = patients.filter(p => (p.registered || "").startsWith(thisMonth)).length;
  const totalTypes    = dashStats?.stats?.test_types || 0;

  const recent   = dashStats?.reports || [];

  // Build type counts from recent reports (from API)
  const typeCount = recent.reduce((acc, r) => {
    if (r.type) acc[r.type] = (acc[r.type] || 0) + 1;
    return acc;
  }, {});


  return (
    <div>
      {/* Welcome */}
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: "#0F172A", marginBottom: 2 }}>
          Welcome, {ROLE_LABEL[user?.role] || user?.username}
        </h1>
        <p style={{ color: "#64748B", fontSize: 13 }}>
          Nashman Institute — Pathology &amp; Regenerative Medicine EMR
        </p>
      </div>

      {/* Stats */}
      <div className="stat-grid">
        {[
          { label: "Total Patients",    value: totalPatients, sub: `${newThisMonth} new this month`, color: "#4F46E5" },
          { label: "Total Reports",     value: totalReports,  sub: "All uploaded",                   color: "#3730a3" },
          { label: "Report Categories", value: totalTypes,    sub: "Test types",                     color: "#1E293B" },
        ].map(s => (
          <div className="stat-card" key={s.label} style={{ borderTop: `3px solid ${s.color}` }}>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
            <div className="stat-sub">{s.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 280px", gap: 16, marginBottom: 16 }}>
        {/* Recent Reports */}
        <div className="card">
          <div className="card-header">
            <div style={{ fontWeight: 700, fontSize: 15 }}>Recent Lab Reports</div>
            <button className="btn btn-ghost btn-sm" onClick={() => setActive("reports")}>View all →</button>
          </div>
          <div>
            {recent.length === 0 ? (
              <div className="empty-state"><p>No reports yet. Upload the first lab report.</p></div>
            ) : recent.map(r => {
              return (
                <div key={r.report_id} className="patient-row"
                  onClick={() => { setSelectedPatient(r.patient_id); setActive("patients"); }}>
                  <TypeDot type={r.type} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 1 }}>{r.patient_name}</div>
                    <div style={{ fontSize: 12, color: "#64748B" }}>{r.type} · {r.lab}</div>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--slate-400)", flexShrink: 0 }}>{r.date}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* By Report Type */}
        <div>
          <div className="card" style={{ marginBottom: 14 }}>
            <div className="card-header">
              <div style={{ fontWeight: 700, fontSize: 14 }}>By Report Type</div>
            </div>
            <div className="card-body">
              {Object.keys(typeCount).length === 0 ? (
                <div style={{ color: "#94A3B8", fontSize: 13, textAlign: "center", padding: "12px 0" }}>No reports</div>
              ) : Object.entries(typeCount).map(([type, count]) => {
                const meta = TYPE_META[type] || TYPE_META.Other;
                const pct  = recent.length > 0 ? Math.round((count / recent.length) * 100) : 0;
                return (
                  <div key={type} style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <TypeDot type={type} />
                        <span style={{ fontSize: 13, fontWeight: 500 }}>{type}</span>
                      </div>
                      <span style={{ fontSize: 12, fontWeight: 700, color: meta.color }}>{count}</span>
                    </div>
                    <div style={{ height: 4, background: "#F1F5F9", borderRadius: 99, overflow: "hidden" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: meta.color, borderRadius: 99 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>


    </div>
  );
}